import { Home } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import defaultAxios, { AxiosError } from "axios";
import { useCallback, useState } from "react";
import { useMutation } from "react-query";
import Swal from "sweetalert2";
import type { ContentGetKantungParkir } from "../../constants/types";
import { useAuth } from "../../context/authContext";
import axios from "../../services/axios";
import useKantungParkir from "../../services/queries/useKantungParkir";
import useUser from "../../services/queries/useUser";
import Spinner from "../Spinner/Spinner";
import KantongCard from "./KantongCard";

interface IModalPilihKantongProps {
    open: boolean;
    onClose: () => void;
}

const ModalPilihKantong = ({ open, onClose }: IModalPilihKantongProps) => {
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { ukmIdUser } = useAuth();
    const { data: user } = useUser();

    const { data: kantungParkir, isLoading: isLoadingKantungParkir } =
        useKantungParkir({
            page: 1,
            size: 100,
        });

    const [selectedKantong, setSelectedKantong] =
        useState<ContentGetKantungParkir | null>(null);

    const handleSelectKantong = (kantong: ContentGetKantungParkir) =>
        setSelectedKantong(kantong);

    const startShift = useMutation({
        mutationFn: async () => {
            await axios.post("/api/shift-start", {
                ukmId: ukmIdUser?.[0],
                userId: user?.userId,
                kantungParkir: selectedKantong?.id,
            });
        },
        onSuccess: () => {
            Swal.fire({
                title: "Shift berhasil dimulai!",
                position: "top-end",
                showConfirmButton: false,
                icon: "success",
                toast: true,
                timer: 3000,
                timerProgressBar: true,
                showCloseButton: true,
                customClass: {
                    container: "my-swal",
                },
            });
            onClose();
        },
        onError: (error) => {
            handleErrorResponse(error);
        },
    });

    const handleErrorResponse = useCallback((error) => {
        if (defaultAxios.isAxiosError(error)) {
            const serverError = error as AxiosError<any>;
            if (serverError && serverError.response) {
                Swal.fire({
                    title: "Terjadi Kesalahan!",
                    text: `${serverError.response.data.message}`,
                    icon: "error",
                    confirmButtonColor: "#45A779",
                    customClass: {
                        container: "my-swal",
                    },
                });
            } else {
                Swal.fire({
                    title: "Terjadi Kesalahan!",
                    text: `Telah Terjadi Kesalahan`,
                    icon: "error",
                    confirmButtonColor: "#45A779",
                    customClass: {
                        container: "my-swal",
                    },
                });
            }
        }
    }, []);

    return (
        <Dialog
            maxWidth="md"
            fullScreen={isPhoneScreen}
            fullWidth={true}
            open={open}
            onClose={() => {}}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle
                sx={{
                    justifyContent: "center",
                    textAlign: "center",
                    fontWeight: 600,
                    fontSize: 24,
                }}
            >
                Pilih Kantong Parkir
            </DialogTitle>
            {isLoadingKantungParkir ? (
                <DialogContent>
                    <Spinner />
                </DialogContent>
            ) : (
                <>
                    <DialogContent sx={{ mt: 4, mb: 1 }}>
                        <Stack
                            direction="row"
                            gap={2}
                            flexWrap="wrap"
                            justifyContent="center"
                        >
                            {kantungParkir?.content.map((li) => (
                                <KantongCard
                                    key={li.id}
                                    data={li}
                                    selectedKantong={selectedKantong}
                                    handleSelectKantong={handleSelectKantong}
                                    icon={
                                        <Home sx={{ width: 56, height: 56 }} />
                                    }
                                />
                            ))}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ padding: 3 }}>
                        <LoadingButton
                            disabled={!selectedKantong}
                            loading={startShift.isLoading}
                            variant="contained"
                            onClick={() => {
                                if (selectedKantong) {
                                    startShift.mutate();
                                } else {
                                    Swal.fire({
                                        title: "Pilih Kantong Parkir",
                                        text: "Anda harus memilih kantong parkir terlebih dahulu.",
                                        icon: "warning",
                                        confirmButtonColor: "#45A779",
                                        customClass: {
                                            container: "my-swal",
                                        },
                                    });
                                }
                            }}
                        >
                            Simpan
                        </LoadingButton>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default ModalPilihKantong;
